import { api } from 'src/api/axios';
import {
  Application,
  ApplicationFirstLoad,
  SubscriptionPlan,
} from './interfaces/application';
import { ApplicationStateResponse, EstimationResult } from './interfaces/ai';
import { Tier } from './interfaces/tier';
import { parseISO, isValid } from 'date-fns';
import { validateAgreementSignature } from '@utils/agreementSignature';

// Simple helper: if effectiveDate is in the past, set to null
const cleanEffectiveDate = (data: Application): Application => {
  if (!data.phq?.effectiveDate) return data;

  try {
    const parsedDate = parseISO(data.phq.effectiveDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!isValid(parsedDate) || parsedDate < today) {
      return {
        ...data,
        phq: { ...data.phq, effectiveDate: null },
      };
    }
  } catch (e) {
    return {
      ...data,
      phq: { ...data.phq, effectiveDate: null },
    };
  }

  return data;
};

export const applicationService = {
  getApplications: async (): Promise<ApplicationFirstLoad> => {
    const response = await api.get<ApplicationFirstLoad>(
      '/user/applications/current'
    );
    return response.data;
  },

  getApplicationsList: async (): Promise<ApplicationFirstLoad[]> => {
    const response =
      await api.get<ApplicationFirstLoad[]>('/user/applications');
    return response.data;
  },

  getEstimation: async (
    id: number | string
  ): Promise<{
    details: any;
    status: string;
    priceId: number;
    tier: Tier;
  }> => {
    const response = await api.post<any>(`/user/applications/${id}/estimate`);
    return response.data;
  },

  applicationState: async () => {
    const response = await api.get<ApplicationStateResponse>(
      '/user/application-state'
    );
    return response.data;
  },

  createApplication: async (data: Application): Promise<Application> => {
    const cleanedData = cleanEffectiveDate(data);
    const response = await api.post<Application>(
      '/user/applications',
      cleanedData
    );
    return response.data;
  },

  getActiveSubscriptionPlans: async (): Promise<SubscriptionPlan[]> => {
    const response = await api.get<SubscriptionPlan[]>(
      '/user/subscription-plans/active'
    );
    return response.data;
  },

  updateApplication: async (data: Application): Promise<Application> => {
    const cleanedData = cleanEffectiveDate(data);
    const response = await api.patch<Application>(
      '/user/applications',
      cleanedData
    );
    return response.data;
  },

  updateApplicationById: async (
    id: number | string,
    data: Application
  ): Promise<Application> => {
    const cleanedData = cleanEffectiveDate(data);
    const response = await api.patch<Application>(
      `/user/applications/${id}`,
      cleanedData
    );
    return response.data;
  },

  submitApplication: async (
    id: number | string,
    data: { paymentMethodId?: string; signature?: string }
  ): Promise<Application> => {
    const payload: {
      paymentMethodId: string;
      signature?: string;
    } = {
      paymentMethodId: data.paymentMethodId || '',
    };
    if (data.signature !== undefined) {
      const signatureValidation = validateAgreementSignature(data.signature);
      if (!signatureValidation.isValid) {
        throw new Error(
          signatureValidation.error || 'Agreement signature is invalid.'
        );
      }
      payload.signature = signatureValidation.value;
    }

    const response = await api.post<Application>(
      `/user/applications/${id}/submit`,
      payload
    );
    return response.data;
  },

  acceptApplication: async (): Promise<void> => {
    await api.post('/applications/accept');
  },

  rejectApplication: async (): Promise<void> => {
    await api.post('/applications/reject');
  },

  resetAcceptance: async (): Promise<void> => {
    await api.post('/user/application-state/reset-acceptance');
  },

  resetArchivedPlans: async (id: number | string): Promise<void> => {
    await api.post(`/user/applications/${id}/reset-archived-plans`);
  },

  getQuote: async (id: number | string): Promise<void> => {
    await api.post(`/user/applications/${id}/quote`);
  },

  updateAddressProgress: async (id: number | string): Promise<void> => {
    await api.post(`/user/applications/${id}/address`);
  },

  updatePhqProgress: async (id: number | string): Promise<void> => {
    await api.post(`/user/applications/${id}/phq`);
  },

  getAllowedPrices: async (
    applicationId: number | string,
    planId: number,
    coverage: number
  ): Promise<
    Array<{
      id: number;
      price: number;
      coverage: number;
      tier: string | null;
      data: Record<string, unknown> | null;
      archivedAt: string | null;
    }>
  > => {
    const response = await api.get(
      `/user/applications/${applicationId}/plans/${planId}/prices/${coverage}`
    );
    return response.data;
  },

  getContractPdf: async (id: number | string): Promise<Blob> => {
    const response = await api.get(`/user/applications/${id}/contract/pdf`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  getContractHtml: async (id: number | string): Promise<string> => {
    const response = await api.get<string | { html?: string }>(
      `/user/applications/${id}/contract/html`
    );
    const data = response.data;
    if (typeof data === 'string') return data;
    return data.html || '';
  },

  getPayloadPdf: async (id: number | string): Promise<Blob> => {
    const response = await api.get(`/user/applications/${id}/payload/pdf`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
};
